import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useAuth } from "./core/Auth";
import {
  getUserFullProfile,
  registerTargetUserType,
  switchUserType,
} from "sr/utils/api/user/user";
import { AppDispatch } from "sr/redux/store";
import { setRegionIdMe } from "sr/helpers/functions/setCityMe";
import { useSocket } from "app/pages/component/SocketContext";

const profiles = [
  {
    userType: "cleaner",
    label: "Cleaner",
    registeredKey: "cleanerRegistered",
    switchPath: "/cleaner-jobs",
    registerPath: "/cleaner/onboarding",
  },
  {
    userType: "company",
    label: "Company",
    registeredKey: "companyRegistered",
    switchPath: "/jobs-listing",
    registerPath: "/company/onboarding",
  },
  {
    userType: "individual",
    label: "Residential Customer",
    registeredKey: "individualRegistered",
    switchPath: "/individual/jobs",
    registerPath: "/individual/onboarding",
  },
];

export const ProfileSwitcherOptions = ({ currentUserType, className = "" }) => {
  const { persistToken } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const socket = useSocket();
  const [registeredModules, setRegisteredModules] = useState({});
  const [sharedRegionId, setSharedRegionId] = useState<string | null>(null);
  const [profileStatusLoaded, setProfileStatusLoaded] = useState(false);
  const [profileStatusError, setProfileStatusError] = useState(false);
  const [loadingUserType, setLoadingUserType] = useState(null);
  const alternateProfiles = profiles.filter(
    (profile) => profile.userType !== currentUserType
  );

  useEffect(() => {
    let isMounted = true;

    getUserFullProfile()
      .then((response) => {
        if (!isMounted) return;
        if (response?.success) {
          setRegisteredModules(response.data?.registeredModule || {});
          const rawRegionId = response.data?.regions?.[0]?.regionId;
          setSharedRegionId(
            typeof rawRegionId === "object"
              ? rawRegionId?.id || rawRegionId?._id || null
              : rawRegionId || null
          );
        } else {
          setProfileStatusError(true);
        }
      })
      .catch(() => {
        if (isMounted) setProfileStatusError(true);
      })
      .finally(() => {
        if (isMounted) setProfileStatusLoaded(true);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleProfileAction = async (profile) => {
    if (loadingUserType) return;
    setLoadingUserType(profile.userType);
    try {
      const isRegistered = registeredModules?.[profile.registeredKey];
      const response = isRegistered
        ? await switchUserType(profile.userType)
        : await registerTargetUserType(profile.userType);
      const auth = response?.data?.auth;
      const userId = auth?.user_id;
      const userType = response?.data?.user?.type;

      if (
        !response?.success ||
        !auth?.jwt ||
        !auth?.jwt_refresh ||
        !userId ||
        userType !== profile.userType
      ) {
        toast.error(
          response?.error?.message ||
            response?.msg ||
            "Unable to change profile."
        );
        return;
      }

      const rawRegionId = response?.data?.user?.regionId;
      const regionId =
        (typeof rawRegionId === "object"
          ? rawRegionId?.id || rawRegionId?._id
          : rawRegionId) || sharedRegionId;

      dispatch({ type: "RESET_STATE" });
      setRegionIdMe(
        userType === "cleaner" || userType === "individual"
          ? regionId || null
          : null
      );
      document.cookie = `refreshToken=${auth.jwt_refresh}; path=/; SameSite=Strict`;
      localStorage.setItem(
        "auth",
        JSON.stringify({ userType, user_id: userId })
      );
      persistToken(
        auth.jwt,
        auth.onBoardingStatus ?? response?.data?.onBoardingStatus
      );
      socket?.disconnect();
      socket?.connect();
      navigate(isRegistered ? profile.switchPath : profile.registerPath, {
        replace: true,
      });
    } catch (error) {
      toast.error("Unable to change profile. Please try again.");
    } finally {
      setLoadingUserType(null);
    }
  };

  return (
    <div
      className={`mt-2 rounded-lg border border-slate-200 bg-slate-50 p-1.5 ${className}`}
    >
      {!profileStatusLoaded ? (
        alternateProfiles.map((profile) => (
          <div
            key={profile.userType}
            className="flex items-center justify-between rounded-md px-2.5 py-2 animate-pulse"
          >
            <span className="space-y-1.5">
              <span className="block h-3.5 w-28 rounded bg-slate-200" />
              <span className="block h-2.5 w-20 rounded bg-slate-200" />
            </span>
            <span className="h-6 w-16 rounded-full bg-slate-200" />
          </div>
        ))
      ) : profileStatusError ? (
        <p className="px-2.5 py-3 text-xs text-slate-500">
          Unable to load profiles. Reopen the menu to retry.
        </p>
      ) : (
        alternateProfiles.map((profile) => {
          const isRegistered = registeredModules?.[profile.registeredKey];

          return (
            <button
              key={profile.userType}
              type="button"
              disabled={Boolean(loadingUserType)}
              onClick={() => handleProfileAction(profile)}
              className="flex w-full items-center justify-between rounded-md px-2.5 py-2 text-left hover:bg-white disabled:opacity-60"
            >
              <span>
                <span className="block text-sm font-semibold text-slate-800">
                  {profile.label}
                </span>
                <span className="block pt-0.5 text-xs text-slate-500">
                  {isRegistered ? "Existing profile" : "Create profile"}
                </span>
              </span>
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                  isRegistered
                    ? "bg-[#e5f4fb] text-[#0078ae]"
                    : "bg-[#fff0f8] text-[#df238e]"
                }`}
              >
                {loadingUserType === profile.userType
                  ? "Please wait..."
                  : isRegistered
                  ? "Switch"
                  : "Register"}
              </span>
            </button>
          );
        })
      )}
    </div>
  );
};
